import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { fetchRevenueStats } from '../../services/adminService';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import './AdminRevenue.css';

const AdminRevenue = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [quickFilter, setQuickFilter] = useState('all'); // 'today', '7days', '30days', 'all'
    const [vehicleFilter, setVehicleFilter] = useState('ALL');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await fetchRevenueStats();
            if (res && res.ok && Array.isArray(res.data)) {
                setTransactions(res.data);
            } else if (Array.isArray(res?.data)) {
                setTransactions(res.data);
            } else {
                setTransactions([]);
            }
        } catch (error) {
            console.error('Error fetching revenue statistics:', error);
            toast.error('Không thể nạp dữ liệu báo cáo doanh thu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Helper functions to match vehicle types in both English and Vietnamese
    const isMotorbike = (type) => {
        const t = (type || '').trim().toLowerCase();
        return t === 'motorcycle' || t.includes('xe máy') || t.includes('xe may') || t.includes('motor');
    };

    const isCar = (type) => {
        const t = (type || '').trim().toLowerCase();
        return t === 'car' || t.includes('ô tô') || t.includes('o to') || (t.includes('car') && !t.includes('motorcycle'));
    };

    // Filter logic
    const filteredTransactions = useMemo(() => {
        let result = [...transactions];

        const now = new Date();

        if (quickFilter === 'today') {
            const todayStr = now.toISOString().split('T')[0];
            result = result.filter(t => new Date(t.timeOut || t.timeIn).toISOString().split('T')[0] === todayStr);
        } else if (quickFilter === '7days') {
            const sevenDaysAgo = new Date(now.getTime() - 7 * 86400000);
            result = result.filter(t => new Date(t.timeOut || t.timeIn) >= sevenDaysAgo);
        } else if (quickFilter === '30days') {
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 86400000);
            result = result.filter(t => new Date(t.timeOut || t.timeIn) >= thirtyDaysAgo);
        }

        if (startDate) {
            const start = new Date(startDate);
            result = result.filter(t => new Date(t.timeOut || t.timeIn) >= start);
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            result = result.filter(t => new Date(t.timeOut || t.timeIn) <= end);
        }

        if (vehicleFilter !== 'ALL') {
            result = result.filter(t => {
                if (vehicleFilter === 'MOTOR') return isMotorbike(t.vehicleType);
                if (vehicleFilter === 'CAR') return isCar(t.vehicleType);
                return (t.vehicleType || '').toLowerCase().includes(vehicleFilter.toLowerCase());
            });
        }

        return result;
    }, [transactions, quickFilter, vehicleFilter, startDate, endDate]);

    // KPI Metrics calculation
    const metrics = useMemo(() => {
        const totalRevenue = filteredTransactions.reduce((sum, t) => sum + (t.price || 0), 0);
        const totalCount = filteredTransactions.length;
        const motorRevenue = filteredTransactions.filter(t => isMotorbike(t.vehicleType)).reduce((sum, t) => sum + (t.price || 0), 0);
        const carRevenue = filteredTransactions.filter(t => isCar(t.vehicleType)).reduce((sum, t) => sum + (t.price || 0), 0);
        const packageRevenue = filteredTransactions.filter(t => t.id && t.id.startsWith('PKG-')).reduce((sum, t) => sum + (t.price || 0), 0);
        const avgPrice = totalCount > 0 ? Math.round(totalRevenue / totalCount) : 0;

        return { totalRevenue, totalCount, motorRevenue, carRevenue, packageRevenue, avgPrice };
    }, [filteredTransactions]);

    // Chart daily aggregated data
    const chartData = useMemo(() => {
        const map = {};
        filteredTransactions.forEach(t => {
            const dateStr = new Date(t.timeOut || t.timeIn).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
            map[dateStr] = (map[dateStr] || 0) + (t.price || 0);
        });

        const sortedKeys = Object.keys(map).reverse().slice(-14);
        return sortedKeys.map(key => ({
            date: key,
            "Doanh thu": map[key]
        }));
    }, [filteredTransactions]);

    // Pagination slice
    const paginatedTransactions = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredTransactions.slice(start, start + pageSize);
    }, [filteredTransactions, currentPage]);

    const totalPages = Math.ceil(filteredTransactions.length / pageSize) || 1;

    const formatCurrency = (val) => {
        return (val || 0).toLocaleString('vi-VN') + ' đ';
    };

    const formatDate = (dateVal) => {
        if (!dateVal) return '-';
        try {
            return new Date(dateVal).toLocaleString('vi-VN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return String(dateVal);
        }
    };

    // Export to Excel (CSV with UTF-8 BOM for Excel compatibility)
    const exportToExcel = () => {
        if (filteredTransactions.length === 0) {
            toast.warning('Không có dữ liệu để xuất Excel');
            return;
        }

        const headers = ['STT', 'Mã Giao Dịch', 'Mã Người Dùng', 'Biển Số Xe', 'Loại Xe', 'Khu Bãi Đỗ', 'Thời Gian Vào', 'Thời Gian Ra', 'Giá Vé (VNĐ)', 'Thanh Toán', 'Trạng Thái'];
        const rows = filteredTransactions.map((t, idx) => [
            idx + 1,
            t.id || `TXN-${idx + 1}`,
            t.userCode || 'Khách vãng lai',
            t.licensePlate || '-',
            t.vehicleType || 'Xe máy',
            t.parkingLotName || 'Khu A',
            formatDate(t.timeIn),
            formatDate(t.timeOut),
            t.price || 0,
            t.paymentMethod || 'Ví điện tử',
            t.status || 'Đã thanh toán'
        ]);

        const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `Bao_Cao_Doanh_Thu_SmartPark_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Đã xuất file Excel thành công!');
    };

    // Export PDF Report (Triggers print view styled as document)
    const exportToPDF = () => {
        window.print();
    };

    return (
        <div className="admin-revenue-page">
            <Helmet>
                <title>Thống kê doanh thu vé xe</title>
            </Helmet>

            <Container fluid className="py-4 px-4 no-print-padding">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div>
                        <h3 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-graph-up-arrow text-primary fs-3"></i>
                            Báo Cáo & Thống Kê Doanh Thu Vé Gửi Xe
                        </h3>
                        <p className="text-secondary mb-0">Theo dõi doanh thu thực tế, xuất báo cáo PDF & Excel định kỳ</p>
                    </div>

                    <div className="d-flex gap-2 no-print">
                        <Button 
                            variant="outline-success" 
                            className="rounded-3 px-3 py-2 fw-semibold d-flex align-items-center gap-2 shadow-xs"
                            onClick={exportToExcel}
                        >
                            <i className="bi bi-file-earmark-excel-fill fs-5"></i>
                            <span>Xuất Excel (.xlsx)</span>
                        </Button>
                        <Button 
                            variant="primary" 
                            className="rounded-3 px-3 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={exportToPDF}
                        >
                            <i className="bi bi-printer-fill fs-5"></i>
                            <span>Xuất PDF / In báo cáo</span>
                        </Button>
                    </div>
                </div>

                {/* Printable Header for PDF */}
                <div className="print-only-header text-center mb-4 pb-3 border-bottom">
                    <h2 className="fw-bold text-primary mb-1">HỆ THỐNG QUẢN LÝ BÃI GIỮ XE IUH PARKING</h2>
                    <h4 className="fw-semibold text-dark mb-2">BÁO CÁO CHI TIẾT DOANH THU VÉ GỬI XE</h4>
                    <p className="text-muted small">Thời gian lập báo cáo: {new Date().toLocaleString('vi-VN')}</p>
                </div>


                {/* Metric KPI Cards */}
                <Row className="g-3 mb-4 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5">
                    <Col>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card primary-accent h-100">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Tổng doanh thu</span>
                                    <h5 className="fw-bold text-primary mb-0 mt-1">{formatCurrency(metrics.totalRevenue)}</h5>
                                </div>
                                <div className="metric-icon rounded-circle bg-primary bg-opacity-10 text-primary p-2 px-3">
                                    <i className="bi bi-wallet2 fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card warning-accent h-100">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Doanh thu Xe máy</span>
                                    <h5 className="fw-bold text-warning mb-0 mt-1">{formatCurrency(metrics.motorRevenue)}</h5>
                                </div>
                                <div className="metric-icon rounded-circle bg-warning bg-opacity-10 text-warning p-2 px-3">
                                    <i className="bi bi-bicycle fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card info-accent h-100">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Doanh thu Ô tô</span>
                                    <h5 className="fw-bold text-info mb-0 mt-1">{formatCurrency(metrics.carRevenue)}</h5>
                                </div>
                                <div className="metric-icon rounded-circle bg-info bg-opacity-10 text-info p-2 px-3">
                                    <i className="bi bi-car-front-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card success-accent h-100">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Doanh thu Gói thành viên</span>
                                    <h5 className="fw-bold text-success mb-0 mt-1">{formatCurrency(metrics.packageRevenue)}</h5>
                                </div>
                                <div className="metric-icon rounded-circle bg-success bg-opacity-10 text-success p-2 px-3">
                                    <i className="bi bi-card-checklist fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card dark-accent h-100">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Lượt gửi xe & Gói</span>
                                    <h5 className="fw-bold text-dark mb-0 mt-1">{metrics.totalCount} lượt</h5>
                                </div>
                                <div className="metric-icon rounded-circle bg-secondary bg-opacity-10 text-dark p-2 px-3">
                                    <i className="bi bi-check-circle-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Filter Controls Bar */}
                <Card className="border-0 shadow-sm rounded-4 mb-4 p-3 no-print bg-white">
                    <Row className="g-3 align-items-center">
                        <Col lg={4}>
                            <div className="d-flex gap-1">
                                <Button 
                                    variant={quickFilter === 'today' ? 'primary' : 'light'} 
                                    size="sm" 
                                    className="rounded-pill px-3 fw-semibold border"
                                    onClick={() => setQuickFilter('today')}
                                >
                                    Hôm nay
                                </Button>
                                <Button 
                                    variant={quickFilter === '7days' ? 'primary' : 'light'} 
                                    size="sm" 
                                    className="rounded-pill px-3 fw-semibold border"
                                    onClick={() => setQuickFilter('7days')}
                                >
                                    7 Ngày qua
                                </Button>
                                <Button 
                                    variant={quickFilter === '30days' ? 'primary' : 'light'} 
                                    size="sm" 
                                    className="rounded-pill px-3 fw-semibold border"
                                    onClick={() => setQuickFilter('30days')}
                                >
                                    30 Ngày qua
                                </Button>
                                <Button 
                                    variant={quickFilter === 'all' ? 'primary' : 'light'} 
                                    size="sm" 
                                    className="rounded-pill px-3 fw-semibold border"
                                    onClick={() => setQuickFilter('all')}
                                >
                                    Tất cả
                                </Button>
                            </div>
                        </Col>

                        <Col lg={3}>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control 
                                    type="date" 
                                    size="sm" 
                                    value={startDate} 
                                    onChange={(e) => setStartDate(e.target.value)}
                                    title="Từ ngày"
                                />
                                <span className="text-muted">-</span>
                                <Form.Control 
                                    type="date" 
                                    size="sm" 
                                    value={endDate} 
                                    onChange={(e) => setEndDate(e.target.value)}
                                    title="Đến ngày"
                                />
                            </div>
                        </Col>

                        <Col lg={3}>
                            <Form.Select 
                                size="sm" 
                                value={vehicleFilter} 
                                onChange={(e) => setVehicleFilter(e.target.value)}
                                className="fw-medium text-dark"
                            >
                                <option value="ALL">Tất cả loại xe</option>
                                <option value="MOTOR">Xe máy (Motorcycle)</option>
                                <option value="CAR">Ô tô (Car)</option>
                                <option value="BICYCLE">Xe đạp / Điện</option>
                            </Form.Select>
                        </Col>

                        <Col lg={2} className="text-end">
                            <Button 
                                variant="light" 
                                size="sm" 
                                className="border rounded-3 fw-semibold text-secondary w-100"
                                onClick={() => { setQuickFilter('30days'); setVehicleFilter('ALL'); setStartDate(''); setEndDate(''); }}
                            >
                                <i className="bi bi-arrow-counterclockwise me-1"></i> Xóa lọc
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Revenue Chart Section */}
                <Card className="border-0 shadow-sm rounded-4 mb-4 p-4 bg-white">
                    <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                        <i className="bi bi-bar-chart-line-fill text-primary"></i>
                        Biểu Đồ Doanh Thu Theo Ngày
                    </h5>
                    {chartData.length > 0 ? (
                        <div style={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} />
                                    <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} tickFormatter={(val) => `${val / 1000}k`} />
                                    <RechartsTooltip formatter={(value) => [formatCurrency(value), "Doanh thu"]} />
                                    <Bar dataKey="Doanh thu" fill="#0d6efd" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="text-center py-5 text-muted">Chưa có dữ liệu doanh thu trong khoảng thời gian này</div>
                    )}
                </Card>

                {/* Revenue Detailed Transactions Tables */}
                {(() => {
                    const parkingList = filteredTransactions.filter(t => !t.id.startsWith('PKG-'));
                    const packageList = filteredTransactions.filter(t => t.id.startsWith('PKG-'));

                    return (
                        <>
                            {/* Table 1: Package Subscriptions (ON TOP) */}
                            <Card className="border-0 shadow-sm rounded-4 p-4 bg-white mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                        <i className="bi bi-card-checklist text-success"></i>
                                        1. Chi Tiết Doanh Thu Đăng Ký & Gia Hạn Gói Thành Viên ({packageList.length})
                                    </h5>
                                </div>

                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="success" size="sm" />
                                        <span className="ms-2 text-muted">Đang tải gói thành viên...</span>
                                    </div>
                                ) : packageList.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table hover className="modern-table align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>STT</th>
                                                    <th>Mã Đơn Gói</th>
                                                    <th>Mã Người Dùng</th>
                                                    <th>Tên Gói Dịch Vụ</th>
                                                    <th>Loại Gói</th>
                                                    <th>Phạm Vi Sử Dụng</th>
                                                    <th>Thời Gian Đăng Ký</th>
                                                    <th>Giá Gói</th>
                                                    <th>Phương Thức</th>
                                                    <th>Trạng Thái Gói</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {packageList.map((item, index) => (
                                                    <tr key={`pkg-txn-${index}`}>
                                                        <td>{index + 1}</td>
                                                        <td><span className="fw-bold font-monospace text-success">{item.id}</span></td>
                                                        <td><span className="fw-medium">{item.userCode}</span></td>
                                                        <td>
                                                            <span className="badge bg-success-subtle text-success fw-bold px-2 py-1">
                                                                {item.parkingLotName || 'Gói đỗ xe thành viên'}
                                                            </span>
                                                        </td>
                                                        <td><span className="fw-semibold text-dark">{item.vehicleType}</span></td>
                                                        <td><span className="badge bg-info-subtle text-info border border-info-subtle px-2 py-1">{item.licensePlate === 'Thành viên gói' ? 'Toàn trường' : item.licensePlate}</span></td>
                                                        <td className="small text-secondary">{formatDate(item.timeIn)}</td>
                                                        <td><span className="fw-bold text-success fs-6">{formatCurrency(item.price)}</span></td>
                                                        <td className="small">{item.paymentMethod}</td>
                                                        <td>
                                                            <Badge bg={item.status === 'Đã hủy' ? 'danger-subtle' : 'success-subtle'} className={`${item.status === 'Đã hủy' ? 'text-danger border-danger-subtle' : 'text-success border-success-subtle'} border px-2 py-1`}>
                                                                {item.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-muted small">Chưa có giao dịch mua gói đỗ xe nào</div>
                                )}
                            </Card>

                            {/* Table 2: Parking Tickets */}
                            <Card className="border-0 shadow-sm rounded-4 p-4 bg-white">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                        <i className="bi bi-p-square-fill text-primary"></i>
                                        2. Chi Tiết Lượt Xe & Thanh Toán Theo Lượt ({parkingList.length})
                                    </h5>
                                </div>

                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" size="sm" />
                                        <span className="ms-2 text-muted">Đang tải lượt xe...</span>
                                    </div>
                                ) : parkingList.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table hover className="modern-table align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>STT</th>
                                                    <th>Mã Giao Dịch</th>
                                                    <th>Mã Người Dùng</th>
                                                    <th>Biển Số Xe</th>
                                                    <th>Loại Xe</th>
                                                    <th>Khu Bãi Đỗ</th>
                                                    <th>Thời Gian Vào</th>
                                                    <th>Thời Gian Ra</th>
                                                    <th>Giá Vé</th>
                                                    <th>Phương Thức</th>
                                                    <th>Trạng Thái</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {parkingList.map((item, index) => (
                                                    <tr key={`p-txn-${index}`}>
                                                        <td>{index + 1}</td>
                                                        <td><span className="fw-bold font-monospace text-primary">{item.id}</span></td>
                                                        <td><span className="fw-medium">{item.userCode}</span></td>
                                                        <td>
                                                            <span className="badge bg-dark-subtle text-dark font-monospace px-2 py-1 fs-6">
                                                                {item.licensePlate}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="fw-medium text-dark">
                                                                {isMotorbike(item.vehicleType) ? 'Xe máy' : (isCar(item.vehicleType) ? 'Ô tô' : item.vehicleType)}
                                                            </span>
                                                        </td>
                                                        <td><span className="badge bg-light text-primary border px-2 py-1">Khu {item.parkingLotName}</span></td>
                                                        <td className="small text-secondary">{formatDate(item.timeIn)}</td>
                                                        <td className="small text-secondary">{formatDate(item.timeOut)}</td>
                                                        <td><span className="fw-bold text-success">{formatCurrency(item.price)}</span></td>
                                                        <td className="small">{item.paymentMethod}</td>
                                                        <td>
                                                            <Badge bg="success-subtle" className="text-success border border-success-subtle px-2 py-1">
                                                                {item.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-muted small">Không có lượt xe gửi theo lượt nào</div>
                                )}
                            </Card>
                        </>
                    );
                })()}

                {/* Printable Signature Footer */}
                <div className="print-only-footer mt-5 pt-4 text-end">
                    <Row>
                        <Col className="text-center ms-auto" md={4}>
                            <p className="fw-bold mb-1">NGƯỜI LẬP BÁO CÁO</p>
                            <p className="small text-muted mb-5">(Ký và ghi rõ họ tên)</p>
                            <p className="fw-bold">Ban Quản Lý Bãi Xe IUH Parking</p>
                        </Col>
                    </Row>
                </div>
            </Container>
        </div>
    );
};

export default AdminRevenue;
